import { centroid } from '@turf/turf';
import axios from 'axios';
import { datadogLogs } from '@datadog/browser-logs';
import {
  farm as farmApi,
  cropZone as cropZoneApi,
  field as fieldAPI,
  products as productsApi,
  newProxyReiphisData as reiphisAPI,
  equipments as equipmentsApi,
  tags as tagsApi,
  genProxyResource
} from 'utilities/api';
import {
  MASTERLIST_CROPS_URL,
  CROPWISE_PROXY_URL
} from 'utilities/apiConstants';
import { getAccessToken } from 'utilities/base-auth';
import { ApiResource } from 'utilities/api.types';
import { displayText } from './formatHelpers';

export const getPropertyExtraData = async (cropZoneId: string) => {
  const { promise: cropZonePromise } = (cropZoneApi as ApiResource).fetch(
    cropZoneId
  );
  const { data: cropZone } = await cropZonePromise.catch(error => {
    datadogLogs.logger.warn(
      'PDF export: crop zone fetch failed',
      { cropZoneId },
      error as Error
    );
    return { data: {} };
  });

  const { promise: fieldPromise } = (fieldAPI as ApiResource).fetch(
    cropZone.fieldId
  );
  const { data: field } = await fieldPromise.catch(error => {
    datadogLogs.logger.warn(
      'PDF export: field fetch failed',
      { fieldId: cropZone.fieldId, cropZoneId },
      error as Error
    );
    return { data: {} };
  });

  const { promise: farmPromise } = (farmApi as ApiResource).fetch(
    field.propertyId
  );
  const { data: farm } = await farmPromise.catch(error => {
    datadogLogs.logger.warn(
      'PDF export: farm fetch failed',
      { farmId: field.propertyId, cropZoneId },
      error as Error
    );
    return { data: {} };
  });

  const cropUrl = `${MASTERLIST_CROPS_URL}/crops/${cropZone.cropId}?location=@CWF`;
  const { data: crop } = await axios
    .get(cropUrl, {
      headers: { common: { Authorization: `Bearer ${getAccessToken()}` } }
    })
    .catch(error => {
      datadogLogs.logger.warn(
        'PDF export: crop fetch failed',
        { cropId: cropZone.cropId, cropZoneId },
        error as Error
      );
      return { data: {} };
    });

  const centroId = cropZone.geometry ? centroid(cropZone.geometry) : {};

  return { cropZone, field, farm, crop, centroId };
};

export const getProductsExtraData = async (productId: string) => {
  const { promise: productPromise } = (productsApi as ApiResource).fetch(
    productId
  );
  const { data: masterProduct } = await productPromise.catch(() => ({
    data: {}
  }));

  const {
    promise: activeIngredientsPromise
  } = (productsApi as ApiResource).fetch(`${productId}/activeIngredients`);

  const {
    data: activeIngredients
  } = await activeIngredientsPromise.catch(() => ({ data: [] }));

  const workerProtection = {
    productName: masterProduct.name,
    key: masterProduct.id,
    signalword: displayText(masterProduct.signalword),
    rup: displayText(masterProduct.restricteduse?.toString()),
    ppe: displayText(masterProduct.ppe),
    ppereentry: displayText(masterProduct.ppereentry)
  };
  const { promise: reiphisPromise } = (reiphisAPI as ApiResource).fetch(
    productId
  );
  const { data: reiphisData } = await reiphisPromise.catch(() => ({
    data: []
  }));
  return {
    masterProduct,
    activeIngredients,
    reiphisData,
    workerProtection
  };
};

export const getEquipmentNames = async (
  equipmentIds: string[]
): Promise<string[]> => {
  if (!equipmentIds || equipmentIds.length === 0) return [];

  const { promise } = (equipmentsApi as ApiResource).fetch();
  const { data } = await promise.catch(() => ({ data: { results: [] } }));

  const equipmentMap = new Map(data.results.map((eq: any) => [eq.id, eq.name]));

  return equipmentIds
    .map(id => equipmentMap.get(id))
    .filter((name): name is string => name !== undefined);
};

export const getPeopleByIds = async (personIds: string[]): Promise<any[]> => {
  if (!personIds || personIds.length === 0) return [];

  const peopleApi = genProxyResource('people', { urlVersion: '/v1/base' });
  const { promise } = (peopleApi as ApiResource).fetch();
  const { data } = await promise.catch(() => ({ data: [] }));

  const personMap = new Map(data.map((person: any) => [person.id, person]));

  return personIds
    .map(id => personMap.get(id))
    .filter((person): person is any => person !== undefined);
};

export const getCompaniesByIds = async (
  companyIds: string[]
): Promise<any[]> => {
  if (!companyIds || companyIds.length === 0) return [];

  const companiesApi = genProxyResource('companies', {
    urlVersion: '/v1/base'
  });
  const { promise } = (companiesApi as ApiResource).fetch();
  const { data } = await promise.catch(() => ({ data: [] }));

  const companyMap = new Map(data.map((company: any) => [company.id, company]));

  return companyIds
    .map(id => companyMap.get(id))
    .filter((company): company is any => company !== undefined);
};

export const getTagsByIds = async (tagIds: string[]): Promise<any[]> => {
  if (!tagIds || tagIds.length === 0) return [];

  const { promise } = (tagsApi as ApiResource).fetch();
  const { data } = await promise.catch(() => ({ data: { results: [] } }));

  const tagMap = new Map(data.results.map((tag: any) => [tag.id, tag]));

  return tagIds
    .map(id => tagMap.get(id))
    .filter((tag): tag is any => tag !== undefined);
};

export const fetchOperationProducts = async (
  operationId: string,
  isWorkOrder: boolean,
  organizationId: string
): Promise<any[]> => {
  if (!operationId || !organizationId) return [];
  const basePath = isWorkOrder ? 'work-orders' : 'applications';
  try {
    const { data } = await axios.get(
      `${CROPWISE_PROXY_URL}/v1/base/${basePath}/${operationId}/products`,
      {
        headers: {
          'Content-Type': 'application/json',
          'cwf-context': JSON.stringify({ organization_id: organizationId }),
          Authorization: `Bearer ${getAccessToken()}`
        }
      }
    );
    return data || [];
  } catch (error) {
    datadogLogs.logger.warn(
      'PDF export: operation products fetch failed',
      { operationId, isWorkOrder, organizationId },
      error as Error
    );
    return [];
  }
};
